import { useAtomValue } from "jotai";
import { FundAtom, SelectedFundAtom } from "@/store/FundStateAtom";
import { ReportingPeriodAtom } from "@/store/ReportingPeriodStateAtom";
import { useState, useEffect } from "react";

export interface DashboardController {
  fundsExist: boolean;
  isLoading: boolean;
  allSetupComplete: boolean;
}

/**
 * Controller hook for the Dashboard component
 * Checks if funds exist in the system and tracks setup completion status
 */
export const useDashboardController = (): DashboardController => {
  const funds = useAtomValue(FundAtom);
  const selectedFund = useAtomValue(SelectedFundAtom);
  const reportingPeriods = useAtomValue(ReportingPeriodAtom);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Determine if all setup steps are complete
  const hasFunds = funds.length > 0;
  const hasFundSelected = !!selectedFund;
  const hasReportingPeriods = reportingPeriods.length > 0;
  const allSetupComplete = hasFunds && hasFundSelected && hasReportingPeriods;

  // Simulate loading and checking if funds exist
  useEffect(() => {
    // Simulate API loading delay
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  return {
    fundsExist: funds.length > 0,
    isLoading,
    allSetupComplete,
  };
};
